import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LogOut, Calendar, MapPin, LayoutDashboard, Plus, Trash2, XCircle, Eye, Check, ExternalLink, Menu, X, Utensils, Ban, Clock, Bot, Globe, Info } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { formatDateStr } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { IconDisplay } from "@/components/IconPicker";

const TABS = [
  { id: "reservations", label: "Reservas", icon: LayoutDashboard },
  { id: "blocked", label: "Fechas Bloqueadas", icon: Calendar },
  { id: "location", label: "Mi Sede", icon: MapPin },
];

export default function SedePanel() {
  const [tab, setTab] = useState("reservations");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, impersonating } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/"); };
  const selectTab = (id) => { setTab(id); setSidebarOpen(false); };

  return (
    <div className={`min-h-screen bg-background flex ${impersonating ? 'pt-10' : ''}`} data-testid="sede-panel">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className={`fixed lg:static inset-y-0 left-0 z-40 w-52 border-r border-border bg-card flex flex-col flex-shrink-0 h-screen transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${impersonating ? 'top-10 h-[calc(100vh-2.5rem)]' : ''}`}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary">
                <Utensils className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm text-foreground">Reservas</div>
                <div className="text-xs text-muted-foreground">Sede</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="lg:hidden h-7 w-7 p-0" onClick={() => setSidebarOpen(false)}><X className="w-4 h-4" /></Button>
          </div>
        </div>
        <nav className="flex-1 py-1 px-2 overflow-y-auto space-y-0.5">
          {TABS.map(t => (
            <button key={t.id} data-testid={`sede-tab-${t.id}`}
              className={`admin-sidebar-item w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg ${tab === t.id ? 'active' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => selectTab(t.id)}>
              <t.icon className="w-4 h-4 flex-shrink-0" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-border px-2 py-3">
          <p className="text-xs text-muted-foreground px-3 pb-2 truncate">{user?.email}</p>
          <Button data-testid="sede-logout-btn" variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-red-500 hover:bg-red-500/10" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesi&oacute;n
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <div className="lg:hidden flex items-center gap-3 p-3 border-b border-border bg-card">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSidebarOpen(true)} data-testid="sede-mobile-menu"><Menu className="w-5 h-5" /></Button>
          <span className="text-sm font-semibold">{TABS.find(t => t.id === tab)?.label || tab}</span>
        </div>
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          {tab === "reservations" && <SedeReservations sedeId={user?.sede_id} />}
          {tab === "blocked" && <BlockedDatesTab sedeId={user?.sede_id} />}
          {tab === "location" && <LocationTab sedeId={user?.sede_id} />}
        </div>
      </div>
    </div>
  );
}

function isReservationPast(r) {
  try {
    const t = r.time?.trim().toUpperCase() || "";
    let h = 0, m = 0;
    if (t.includes("AM") || t.includes("PM")) {
      const parts = t.replace(/[AP]M/g, "").trim().split(":");
      h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
      if (t.includes("PM") && h !== 12) h += 12;
      else if (t.includes("AM") && h === 12) h = 0;
    } else {
      const parts = t.split(":"); h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
    }
    const [y, mo, d] = (r.date || "").split("-").map(Number);
    return new Date(y, mo - 1, d, h, m) < new Date();
  } catch { return false; }
}

function SedeReservations({ sedeId }) {
  const [reservations, setReservations] = useState([]);
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [detailModal, setDetailModal] = useState(null);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const load = useCallback(() => {
    const params = { page, per_page: perPage };
    if (filterDateStart) params.date_start = filterDateStart;
    if (filterDateEnd) params.date_end = filterDateEnd;
    if (filterStatus !== "all") params.status = filterStatus;
    API.get("/reservations", { params }).then(r => {
      const d = r.data;
      setReservations(d.items || d);
      setTotalPages(d.total_pages || 1);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [filterDateStart, filterDateEnd, filterStatus, page, perPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filterDateStart, filterDateEnd, filterStatus, perPage]);

  const handleConfirm = async (id) => {
    try { await API.post(`/reservations/${id}/confirm`); toast.success("Reserva confirmada"); setDetailModal(null); load(); }
    catch { toast.error("Error"); }
  };

  const handleCancel = async (id, reason) => {
    try {
      await API.post(`/reservations/${id}/cancel`, { reason, cancelled_by: "sede" });
      toast.success("Reserva cancelada"); setDetailModal(null); load();
    } catch { toast.error("Error al cancelar"); }
  };

  const handleCancelSubmit = () => {
    if (!cancelReason.trim()) { toast.error("Ingresa un motivo de cancelaci\u00f3n"); return; }
    handleCancel(detailModal?.id, cancelReason);
    setShowCancelForm(false);
    setCancelReason("");
  };

  const handleCloseDetail = () => {
    setShowCancelForm(false);
    setCancelReason("");
    setDetailModal(null);
  };

  const statusBadge = (status) => {
    const map = {
      created: { cls: "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-700", text: "Creada" },
      confirmed: { cls: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700", text: "Confirmada" },
      cancelled: { cls: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-700", text: "Cancelada" },
      informational: { cls: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700", text: "Informativo" },
    };
    const s = map[status] || map.created;
    return <Badge variant="outline" className={`font-medium ${s.cls}`}>{s.text}</Badge>;
  };

  return (
    <div data-testid="sede-reservations">
      <h2 className="text-2xl font-bold mb-6">Reservas de mi Sede</h2>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Desde</Label>
          <Input type="date" value={filterDateStart} onChange={e => setFilterDateStart(e.target.value)} className="w-40 bg-background border-border" data-testid="sede-filter-date-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Hasta</Label>
          <Input type="date" value={filterDateEnd} onChange={e => { if (filterDateStart && e.target.value < filterDateStart) { toast.error("Fecha final anterior a inicial"); return; } setFilterDateEnd(e.target.value); }} className="w-40 bg-background border-border" data-testid="sede-filter-date-end" />
        </div>
        {(filterDateStart || filterDateEnd) && (
          <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={() => { setFilterDateStart(""); setFilterDateEnd(""); }}>Limpiar</Button>
        )}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-background border-border" data-testid="sede-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="created">Creadas</SelectItem>
            <SelectItem value="confirmed">Confirmadas</SelectItem>
            <SelectItem value="cancelled">Canceladas</SelectItem>
            <SelectItem value="informational">Informativas</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-full overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">C&oacute;digo</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Nombre</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Email</TableHead>
              <TableHead className="text-muted-foreground">Fecha</TableHead>
              <TableHead className="text-muted-foreground">Hora</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Personas</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Origen</TableHead>
              <TableHead className="text-muted-foreground">Estado</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservations.map(r => {
              const past = isReservationPast(r);
              return (
                <TableRow key={r.id} data-testid={`sede-reservation-row-${r.id}`} className={`border-border cursor-pointer hover:bg-muted/50 ${past ? 'opacity-60' : ''}`} onClick={() => setDetailModal(r)}>
                  <TableCell className="text-primary font-medium text-xs">{r.reservation_code}</TableCell>
                  <TableCell className="text-sm hidden md:table-cell">{r.full_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{r.email}</TableCell>
                  <TableCell className="text-sm">{formatDateStr(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.time}</TableCell>
                  <TableCell className="text-sm hidden md:table-cell">{r.num_people}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {r.source === "chatbot" ? (
                      <Badge variant="outline" className="text-[10px] bg-blue-500/15 text-blue-400 border-blue-500/40">
                        <Bot className="w-3 h-3 mr-1" /> Chatbot
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/40">
                        <Globe className="w-3 h-3 mr-1" /> Web
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-xs" onClick={() => setDetailModal(r)} title="Ver detalle" data-testid={`sede-view-reservation-${r.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {reservations.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No hay reservas</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 text-sm">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground">{total} reservas</span>
          <Select value={String(perPage)} onValueChange={v => setPerPage(Number(v))}>
            <SelectTrigger className="w-20 h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">por p&aacute;gina</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </div>
      </div>
      {/* Reservation Detail Modal with Actions */}
      <Dialog open={!!detailModal} onOpenChange={handleCloseDetail}>
        <DialogContent className="bg-card border-border sm:max-w-md w-full max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg gap-0 sm:gap-4" data-testid="sede-reservation-detail-modal">
          <DialogHeader className="p-4 sm:p-0 border-b sm:border-0 border-border sticky top-0 bg-card z-10">
            <DialogTitle className="text-lg">Detalle de Reserva</DialogTitle>
          </DialogHeader>
          {detailModal && (() => {
            const fmt = (isoStr) => {
              if (!isoStr) return "-";
              try { const d = new Date(isoStr); if (isNaN(d.getTime())) return isoStr; const p = (n) => String(n).padStart(2,"0"); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`; } catch { return isoStr; }
            };
            const stMap = {created:"Creada",confirmed:"Confirmada",cancelled:"Cancelada",informational:"Informativo"};
            const stCls = {created:"bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",confirmed:"bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",cancelled:"bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",informational:"bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"};
            const past = isReservationPast(detailModal);
            return (
              <div className="p-4 sm:p-0 space-y-1">
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-sm text-muted-foreground">Estado</span>
                  <span className={`text-sm font-semibold px-2 py-0.5 rounded-md ${stCls[detailModal.status]||''}`}>{stMap[detailModal.status]}</span>
                </div>
                {[
                  ["C\u00f3digo", detailModal.reservation_code],
                  ["Nombre", detailModal.full_name], ["Email", detailModal.email], ["Tel\u00e9fono", detailModal.phone],
                  ["Fecha", detailModal.date], ["Hora", detailModal.time],
                  ["Personas", detailModal.num_people], ["Motivo", detailModal.reservation_purpose],
                  ...(detailModal.zone_type ? [["Zona", detailModal.zone_type]] : []),
                  ...(detailModal.comments ? [["Comentarios", detailModal.comments]] : []),
                  ...(detailModal.cancellation_reason ? [["Motivo cancelaci\u00f3n", detailModal.cancellation_reason]] : []),
                  ["Fecha de creaci\u00f3n", fmt(detailModal.created_at)],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between py-2 border-b border-border/30 last:border-0">
                    <span className="text-sm text-muted-foreground">{l}</span>
                    <span className="text-sm font-medium text-right max-w-[60%] break-words">{v}</span>
                  </div>
                ))}
                {showCancelForm && (
                  <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-red-500 flex items-center gap-2"><XCircle className="w-4 h-4" /> Cancelar reserva</p>
                    <Textarea
                      data-testid="sede-detail-cancel-reason"
                      value={cancelReason}
                      onChange={e => setCancelReason(e.target.value)}
                      className="bg-background border-border"
                      placeholder="Ingresa el motivo de la cancelaci&oacute;n..."
                      rows={3}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setShowCancelForm(false); setCancelReason(""); }}>Volver</Button>
                      <Button size="sm" variant="destructive" onClick={handleCancelSubmit} disabled={!cancelReason.trim()} data-testid="sede-confirm-cancel-btn">Confirmar cancelaci&oacute;n</Button>
                    </div>
                  </div>
                )}
                {!showCancelForm && (
                  <div className="pt-4 flex flex-wrap gap-2 justify-end">
                    {detailModal.status === "informational" && (
                      <span className="text-xs text-amber-500 mr-auto self-center flex items-center gap-1" data-testid="sede-informational-note">
                        <Info className="w-3.5 h-3.5" /> Reserva informativa (corporativa): no admite acciones.
                      </span>
                    )}
                    {!past && detailModal.status === "created" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleConfirm(detailModal.id)} data-testid="sede-detail-confirm-btn">
                        <Check className="w-4 h-4 mr-1" /> Confirmar
                      </Button>
                    )}
                    {!past && detailModal.status !== "cancelled" && detailModal.status !== "informational" && (
                      <Button size="sm" variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => setShowCancelForm(true)} data-testid="sede-detail-cancel-btn">
                        <XCircle className="w-4 h-4 mr-1" /> Cancelar reserva
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={handleCloseDetail}>Cerrar</Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlockedDatesTab({ sedeId }) {
  const [dates, setDates] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ date: "", reason: "", start_time: "", end_time: "", is_full_day: true });

  const load = useCallback(() => {
    if (!sedeId) return;
    API.get(`/sedes/${sedeId}/blocked-dates`).then(r => setDates(r.data)).catch(() => {});
  }, [sedeId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date) { toast.error("Selecciona una fecha"); return; }
    if (!form.is_full_day) {
      if (!form.start_time || !form.end_time) { toast.error("Completa hora de inicio y fin"); return; }
      if (form.start_time >= form.end_time) { toast.error("La hora de inicio debe ser menor a la de fin"); return; }
    }
    try {
      await API.post(`/sedes/${sedeId}/blocked-dates`, {
        date: form.date,
        reason: form.reason,
        start_time: form.is_full_day ? null : form.start_time,
        end_time: form.is_full_day ? null : form.end_time,
      });
      toast.success("Bloqueo creado");
      setDialog(false);
      setForm({ date: "", reason: "", start_time: "", end_time: "", is_full_day: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await API.delete(`/sedes/${sedeId}/blocked-dates/${id}`);
      toast.success("Bloqueo eliminado");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="blocked-dates-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Fechas Bloqueadas</h2>
        <Button data-testid="add-blocked-date-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setDialog(true)}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo bloqueo
        </Button>
      </div>
      {!sedeId ? (
        <p className="text-muted-foreground">No tienes una sede asignada.</p>
      ) : (
        <div className="space-y-2">
          {dates.map(d => {
            const isFullDay = !d.start_time && !d.end_time;
            return (
              <div key={d.id} className="flex items-center justify-between bg-card border border-border rounded-lg p-3" data-testid={`blocked-row-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {isFullDay
                    ? <Ban className="w-4 h-4 text-red-400 flex-shrink-0" />
                    : <Clock className="w-4 h-4 text-primary flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{formatDateStr(d.date)}</span>
                      <Badge className={isFullDay ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-primary/10 text-primary border border-primary/20"} variant="outline">
                        {isFullDay ? "Todo el d\u00eda" : `${d.start_time} – ${d.end_time}`}
                      </Badge>
                    </div>
                    {d.reason && <p className="text-xs text-muted-foreground truncate">{d.reason}</p>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-900/20" onClick={() => remove(d.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
          {dates.length === 0 && <p className="text-muted-foreground text-sm py-4">No hay fechas bloqueadas</p>}
        </div>
      )}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Nuevo bloqueo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-date-input" />
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg border border-border">
              <div>
                <Label className="text-sm">Bloquear todo el d&iacute;a</Label>
                <p className="text-xs text-muted-foreground">Desactiva para bloquear solo un rango horario</p>
              </div>
              <button type="button" data-testid="blocked-fullday-toggle"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${form.is_full_day ? 'bg-primary' : 'bg-secondary'}`}
                onClick={() => setForm(p => ({ ...p, is_full_day: !p.is_full_day }))}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${form.is_full_day ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {!form.is_full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Hora inicio</Label>
                  <Input type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-start-time" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Hora fin</Label>
                  <Input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-end-time" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (opcional)</Label>
              <Input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} className="mt-1 bg-background border-border" placeholder="Ej: Mantenimiento, festivo..." data-testid="blocked-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="save-blocked-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Bloquear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationTab({ sedeId }) {
  const [sede, setSede] = useState(null);
  const [allTableTypes, setAllTableTypes] = useState([]);
  const [sedeConditions, setSedeConditions] = useState([]);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  const loadSede = useCallback(() => {
    if (!sedeId) return;
    API.get(`/sedes/${sedeId}`).then(r => {
      setSede(r.data);
      setSettings({
        opening_time: r.data.opening_time || "12:00",
        closing_time: r.data.closing_time || "21:00",
        time_interval: r.data.time_interval || 30,
        max_people: r.data.max_people || 10,
        zone_types_enabled: r.data.zone_types_enabled || false,
        assigned_zone_type_ids: r.data.assigned_zone_type_ids || [],
        show_conditions: r.data.show_conditions !== false,
        conditions_enabled: r.data.conditions_enabled !== false,
      });
    }).catch(() => {});
    API.get(`/conditions?sede_id=${sedeId}`).then(r => setSedeConditions(r.data)).catch(() => setSedeConditions([]));
  }, [sedeId]);

  useEffect(() => {
    loadSede();
    API.get("/zone-types").then(r => setAllTableTypes(r.data)).catch(() => {});
  }, [loadSede]);

  const toggleCondition = async (condId) => {
    try {
      const { data } = await API.patch(`/conditions/${condId}/toggle`);
      setSedeConditions(prev => prev.map(x => x.id === condId ? { ...x, active: data.active } : x));
    } catch { toast.error("Error al cambiar estado"); }
  };

  const toggleTableType = (ttId) => {
    setSettings(p => {
      const ids = [...(p.assigned_zone_type_ids || [])];
      if (ids.includes(ttId)) return { ...p, assigned_zone_type_ids: ids.filter(id => id !== ttId) };
      return { ...p, assigned_zone_type_ids: [...ids, ttId] };
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await API.put(`/sedes/${sedeId}/settings`, settings);
      toast.success("Configuraci\u00f3n actualizada");
      loadSede();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!sedeId) return <p className="text-muted-foreground">No tienes una sede asignada.</p>;
  if (!sede) return <p className="text-muted-foreground">Cargando...</p>;

  return (
    <div data-testid="location-tab">
      <h2 className="text-2xl font-bold mb-6">Mi Sede</h2>
      <div className="max-w-2xl space-y-6">
        <Card className="bg-card border-border overflow-hidden">
          {sede.image_url && <img src={sede.image_url} alt={sede.name} className="w-full h-48 object-cover" />}
          <CardContent className="p-6 space-y-3">
            <h3 className="text-xl font-bold font-heading">{sede.name}</h3>
            <p className="text-sm text-muted-foreground">{sede.description}</p>
            <div className="flex items-center gap-2 text-sm text-primary">
              <MapPin className="w-4 h-4" /> {sede.address}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-lg font-semibold font-heading">Configuraci&oacute;n</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Hora apertura</Label>
                <Input type="time" value={settings.opening_time || ""} onChange={e => setSettings(p => ({ ...p, opening_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="sede-opening-time" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Hora cierre</Label>
                <Input type="time" value={settings.closing_time || ""} onChange={e => setSettings(p => ({ ...p, closing_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="sede-closing-time" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Intervalo (min)</Label>
                <Input type="number" min={5} value={settings.time_interval || 30} onChange={e => setSettings(p => ({ ...p, time_interval: parseInt(e.target.value) || 30 }))} className="mt-1 bg-background border-border" data-testid="sede-interval" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Max personas</Label>
                <Input type="number" min={1} value={settings.max_people || 10} onChange={e => setSettings(p => ({ ...p, max_people: parseInt(e.target.value) || 10 }))} className="mt-1 bg-background border-border" data-testid="sede-max-people" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Condiciones de reserva</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-muted-foreground">Habilitar</span>
                  <button type="button" data-testid="sede-conditions-enabled-toggle" className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${settings.conditions_enabled ? 'bg-primary' : 'bg-secondary'}`}
                    onClick={() => setSettings(p => ({ ...p, conditions_enabled: !p.conditions_enabled, show_conditions: !p.conditions_enabled }))}>
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${settings.conditions_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              </div>
              {settings.conditions_enabled && sedeConditions.length > 0 && (
                <div className="space-y-1 border border-border rounded-lg p-2">
                  {sedeConditions.map(c => (
                    <label key={c.id} className={`flex items-start gap-3 p-2 rounded-lg hover:bg-secondary cursor-pointer min-w-0 ${c.active === false ? 'opacity-50' : ''}`} data-testid={`sede-condition-item-${c.id}`}>
                      <input type="checkbox" checked={c.active !== false} onChange={() => toggleCondition(c.id)} className="rounded border-border mt-1 flex-shrink-0" data-testid={`sede-condition-toggle-${c.id}`} />
                      <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <IconDisplay name={c.icon} className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <span className="text-sm font-medium break-words block">{c.title}</span>
                        <p className="text-xs text-muted-foreground break-words line-clamp-2">{c.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              {settings.conditions_enabled && sedeConditions.length === 0 && (
                <p className="text-xs text-muted-foreground border border-border rounded-lg p-3">Sin condiciones asignadas. Solicita al administrador que agregue condiciones para esta sede.</p>
              )}
            </div>
            {allTableTypes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-muted-foreground">Tipos de zona</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Habilitar</span>
                    <button type="button" data-testid="sede-tt-toggle" className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${settings.zone_types_enabled ? 'bg-primary' : 'bg-secondary'}`}
                      onClick={() => setSettings(p => ({ ...p, zone_types_enabled: !p.zone_types_enabled }))}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${settings.zone_types_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </label>
                </div>
                {settings.zone_types_enabled && (
                  <div className="space-y-1 border border-border rounded-lg p-2">
                    {allTableTypes.map(tt => (
                      <label key={tt.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary cursor-pointer" data-testid={`sede-tt-${tt.id}`}>
                        <input type="checkbox" checked={settings.assigned_zone_type_ids?.includes(tt.id)} onChange={() => toggleTableType(tt.id)} className="rounded border-border" />
                        {tt.image_url && <img src={tt.image_url} alt={tt.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{tt.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{tt.max_seats} personas</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button data-testid="save-sede-settings-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={saveSettings} disabled={saving}>
              {saving ? "Guardando..." : "Guardar configuraci\u00f3n"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
